/**
 * The live preview — the real runtime, in an isolated context.
 *
 * PRD §3.4, verbatim and load-bearing: "preview loads the **real runtime
 * bundle** in an isolated preview context — never a builder-side re-render.
 * Builder/runtime divergence is the defining bug class of every form builder
 * ever shipped."
 *
 * So this is an iframe that loads `/v1/embed.js` — the same file a respondent's
 * browser downloads, staged onto the dashboard Worker by
 * `scripts/stage-embed.mjs` — and hands it the DRAFT through the hosted page's
 * own boot path: a `<script type="application/json" id="mdx-funnel-data">`
 * beside a `<div id="mdx-funnel">`, exactly as `routes/hosted.ts` writes it.
 * Nothing in `packages/app` renders a block, and there is no second renderer to
 * diverge from.
 *
 * WHY AN IFRAME AND NOT A SHADOW ROOT. The runtime already puts itself in a
 * shadow root, so style isolation is not the reason. The reason is that the
 * preview must be a WINDOW: it is sized to 375 and 1440 (ARCH §7.5, "two widths
 * only"), and the runtime's own layout decisions read the viewport. A shadow
 * root inside a 900px dashboard would render the 900px layout at a 375px width,
 * which is precisely the divergence the PRD names.
 *
 * A PREVIEW CANNOT CREATE A LEAD. The payload's `api_origin` is the dashboard's
 * own origin, which serves `/v1/embed.js` and has no `POST /public/f/{fid}/submit`
 * — so a submission pressed in the preview fails at the network and writes
 * nothing. That is deliberate and it is the conservative side of an unspecified
 * question (Q-048): the alternative is previewing against the real submit route,
 * which would put test leads in an operator's own list.
 *
 * Story 6.5.
 */
import { useEffect, useMemo, useRef } from 'react'
import { ApiFailure, apiPost } from '../lib/api'
import { funnelSchema, type Funnel } from '@leadgible/schema'

/**
 * The hosted page's boot contract, restated rather than imported.
 *
 * CLAUDE.md fixes the dependency direction as `schema ← api ← app` and
 * `schema ← embed`: `packages/app` may not import `packages/embed`, and
 * `tests/dependency-rule.test.ts` enforces it. So these two ids are duplicated
 * from `packages/embed/src/scan.ts` on purpose, and the duplication is guarded
 * where it can be — `tests/preview-boot-contract.test.ts` reads both files as
 * TEXT and fails if they ever disagree, which is what a rename on either side
 * would otherwise leave as a silently blank preview.
 */
export const HOSTED_DATA_ID = 'mdx-funnel-data'
export const HOSTED_MOUNT_ID = 'mdx-funnel'

/**
 * Where a funnel is reported — the API's `GET /report`, Story 25.5.
 *
 * A live funnel is handed this link by the server (`reportPageUrl` in
 * `packages/api/src/services/funnels/embed.ts`), and the runtime draws "Report
 * this form" in the badge row from it. The preview hands the runtime its own
 * bootstrap, so it builds the same link — or the row it previews would be a row
 * no respondent gets. Duplicated for the reason the two ids above are, and held
 * to the API's by the same test.
 */
export const REPORT_PATH = '/report'
export const reportUrlFor = (apiOrigin: string, funnelId: string): string =>
  `${apiOrigin}${REPORT_PATH}?funnel=${encodeURIComponent(funnelId)}`

/**
 * The two viewports, taken from `playwright.config.ts` rather than chosen here.
 *
 * ARCH §7.5 fixes "two widths only — 375px and 1440px" and the Playwright
 * projects already pin the heights that go with them (375x812, 1440x900). Using
 * the same pair means the preview is literally the viewport every screenshot in
 * this repo is taken at, and neither number is invented by this file.
 */
export const PREVIEW_VIEWPORTS = [
  { width: 375, height: 812 },
  { width: 1440, height: 900 },
] as const
export const PREVIEW_WIDTHS = [375, 1440] as const
export type PreviewWidth = (typeof PREVIEW_WIDTHS)[number]

const viewportFor = (width: PreviewWidth) =>
  PREVIEW_VIEWPORTS.find((v) => v.width === width) ?? PREVIEW_VIEWPORTS[0]

/**
 * The document the iframe runs.
 *
 * `JSON.stringify` twice over: once for the payload, and once more escaping
 * `</script` so a funnel whose question contains that string cannot close the
 * tag it is inside. The runtime is the only script this page loads and there is
 * no other markup to inject into, but a preview is still a page rendering
 * operator-authored text and RULE-050's habit applies.
 */
export function previewDocument(
  funnel: Funnel,
  apiOrigin: string,
  embedSrc: string,
  /**
   * Story 26.5 — whether a respondent to this funnel is told that AI reads their
   * answers. The server decides it for a live funnel; the caller hands the preview
   * the same answer from the boot payload, so the line is previewed exactly where
   * it will appear and is absent exactly where it will be.
   */
  aiDisclosed = false,
  /** Story 26.6 — the organisation's own sentence after the minimum, as the server sends it. */
  disclosureText: string | null = null,
): string {
  /**
   * PARSED, not passed through. Two reasons, and both are about the preview
   * being the real thing rather than approximately it:
   *
   *   1. the runtime reads `settings.resume.enabled` and its siblings, which
   *      `funnelSchema` supplies by `.default()` and a raw object does not
   *      have. A published snapshot has been through the parser, so the runtime
   *      has never had to cope without them — hand it an unparsed draft and it
   *      throws on the first read, leaving a white rectangle with nothing to
   *      say. Parsing here gives it exactly what publishing would.
   *   2. a draft is mid-edit and can be INVALID — an empty question, an option
   *      deleted out from under a rubric. That is normal while somebody works,
   *      and it must not be a blank panel either.
   */
  const parsed = funnelSchema.safeParse(funnel)
  if (!parsed.success) return cannotPreview(parsed.error.issues[0])

  const payload = JSON.stringify({
    funnel_id: parsed.data.meta.id,
    api_origin: apiOrigin,
    schema: parsed.data,
    // The badge is DECISION-057's and its control is not drawn yet (Q-047);
    // the preview shows what a plan default would show, which is on.
    branding: { badge: true },
    // Story 25.5: every funnel's badge row carries the report link, so the
    // preview's does. The frame is sandboxed without `allow-popups`, so pressing
    // it here opens nothing — it is there to be seen where a respondent sees it.
    report: { url: reportUrlFor(apiOrigin, parsed.data.meta.id) },
    // Story 26.5: the flag the schema response and the hosted page carry. A flag,
    // as theirs is — the sentence is the runtime's own.
    // Story 26.6: and the organisation's own sentence beside it, only while the flag is set.
    disclosure: aiDisclosed && disclosureText ? { ai: true, text: disclosureText } : { ai: aiDisclosed },
    // DECISION-071. The one word that makes Submit reach the probe instead of
    // the network — a transport, not a branch in the runtime.
    submit_via: 'host',
  }).replace(/<\/script/gi, '<\\/script')

  return [
    '<!doctype html>',
    '<html lang="en"><head><meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    '<style>html,body{margin:0;padding:0;background:transparent}</style>',
    '</head><body>',
    `<div id="${HOSTED_MOUNT_ID}"></div>`,
    `<script type="application/json" id="${HOSTED_DATA_ID}">${payload}</script>`,
    `<script src="${embedSrc}"></script>`,
    '</body></html>',
  ].join('')
}

/**
 * What the frame shows when the draft cannot be rendered yet.
 *
 * Names WHICH field, because "preview unavailable" sends somebody hunting
 * across six tabs. Plain markup and no script: this document is precisely the
 * case where the runtime cannot run.
 */
function cannotPreview(
  issue: { path: readonly (string | number)[]; message: string } | undefined,
): string {
  const where = issue?.path.join('.') ?? ''
  const escaped = `${where ? `${where} — ` : ''}${issue?.message ?? 'this draft is not complete yet'}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  /*
    CSS SYSTEM COLOURS, not tokens and not literals. This document runs inside a
    sandboxed frame with no access to the dashboard's stylesheet, so
    `var(--mdx-slate)` would resolve to nothing; and a hex here would be a
    Leadgible colour hardcoded outside `design/tokens.css`, which is the defect
    the token gate exists to catch. `GrayText` and `CanvasText` are the platform's
    own names for exactly these two roles and follow the reader's own contrast
    settings, which is better than either.
  */
  return [
    '<!doctype html>',
    '<html lang="en"><head><meta charset="utf-8">',
    '<style>body{margin:0;padding:24px;font:400 15px/1.6 system-ui,sans-serif;color:GrayText}',
    'strong{display:block;color:CanvasText;margin-bottom:6px}</style>',
    '</head><body>',
    '<strong>Not ready to preview</strong>',
    `<span>${escaped}</span>`,
    '</body></html>',
  ].join('')
}

/** The two message types the frame and its host agree on (`embed/src/submit.ts`). */
const HOST_SUBMIT = 'leadgible:preview:submit'
const HOST_SUBMIT_RESULT = 'leadgible:preview:result'

export function Preview({
  funnel,
  width,
  apiOrigin,
  workspaceId,
  aiDisclosed = false,
  disclosureText = null,
  embedSrc = '/v1/embed.js',
}: {
  funnel: Funnel
  width: PreviewWidth
  apiOrigin: string
  /** Absent only before the shell resolves one; Submit then declines politely. */
  workspaceId: string | null
  /** Story 26.5 — whether this organisation's funnels carry the AI disclosure. */
  aiDisclosed?: boolean
  /** Story 26.6 — the organisation's own sentence in it, or none. */
  disclosureText?: string | null
  embedSrc?: string
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  // Rebuilt whenever the draft changes, which is what makes it live. The whole
  // document is replaced rather than messaged into, because a fresh boot is
  // what a respondent gets and anything else is a second code path.
  const doc = useMemo(
    () => previewDocument(funnel, apiOrigin, embedSrc, aiDisclosed, disclosureText),
    [funnel, apiOrigin, embedSrc, aiDisclosed, disclosureText],
  )
  const viewport = viewportFor(width)

  /**
   * The host half of DECISION-071's bridge.
   *
   * IT VERIFIES `event.source`, not the origin. The frame is sandboxed without
   * `allow-same-origin`, so its origin is the opaque `"null"` and an origin
   * check would either reject everything or accept anything. Identity is the
   * check that means something here: only the window this component created can
   * be the one that sent the message.
   *
   * IT SENDS ANSWERS, NOT A SESSION. The probe call happens here, with the
   * cookie the dashboard already has; the frame never sees a credential.
   */
  useEffect(() => {
    async function onMessage(event: MessageEvent) {
      const data = event.data as { type?: unknown; ticket?: unknown; body?: unknown } | null
      if (!data || data.type !== HOST_SUBMIT || typeof data.ticket !== 'string') return
      if (!frame.current || event.source !== frame.current.contentWindow) return

      const reply = (result: unknown) =>
        (event.source as Window | null)?.postMessage(
          { type: HOST_SUBMIT_RESULT, ticket: data.ticket, result },
          '*',
        )

      if (!workspaceId) return reply(null)

      const answers = (data.body as { answers?: unknown } | null)?.answers
      try {
        const probed = await apiPost<{
          score: number
          band: { id: string; label: string; tone: string }
          verdict: { sentence: string; reason: string }
        }>(`/v1/w/${workspaceId}/funnels/${funnel.meta.id}/probe`, { answers })

        /*
          Shaped as the runtime's own `SubmitResponse`, because the runtime is
          the real one and renders its real outcome page — DECISION-071's "no
          builder-side re-render". The probe writes nothing, so there is no lead
          id to give and `lead_id` is deliberately absent.
        */
        reply({
          outcome: {
            heading: probed.verdict.sentence,
            body: probed.verdict.reason,
            band: probed.band.tone,
            score: probed.score,
          },
        })
      } catch (e) {
        // A refusal, not a crash: the runtime's one branch for "it did not go"
        // renders its own error state, which is what a respondent would see.
        void (e instanceof ApiFailure)
        reply(null)
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [workspaceId, funnel.meta.id])

  return (
    <iframe
      ref={frame}
      title={`Live preview at ${width} pixels`}
      data-preview-width={width}
      srcDoc={doc}
      // `allow-scripts` and nothing else. The runtime needs to run; it does not
      // need same-origin access to the dashboard, forms, popups or navigation,
      // and a preview that could reach the parent document would be a way into
      // a signed-in session from operator-authored content.
      sandbox="allow-scripts"
      /*
        Both numbers are DATA — the viewport being previewed, chosen by the
        operator from the pair the repo already fixes. Neither is a dimension of
        THIS screen, which is why neither is a token.
      */
      style={{ width: `${viewport.width}px`, height: `${viewport.height}px` }}
      className="max-w-full flex-none rounded-mdx-control border border-mdx-line bg-mdx-white"
    />
  )
}
